import { LOGBOOK_WINDOWS } from './logbook';
import { DEFAULT_LOGBOOK_FILTERS, boxAround, type LogbookFilterState } from './logbookFilters';
import type { LogbookAggregationResult, LogbookActorKind, LogbookEvent } from '../types/logbook';

/**
 * "Who has been here?"
 *
 * A server, a position, a radius and a window. The answer is a ranked list of everyone whose
 * evidence fell inside that cube in that time, with when they were first and last seen there.
 *
 * Two sources feed it, and the split matters. The actor aggregation is exact: it counts every
 * matching record in one read transaction and, on a current Loglibrarian, carries the first and last
 * timestamp per actor. The raw page is a sample, capped at 200 records, and is used only for what
 * an aggregation cannot say: which actions each person took, and how close they came to the centre.
 * Counts and presence bounds therefore never come from the sample when the aggregation has them.
 */

export interface LogbookNearbyQuery {
  serverId: string;
  x: number;
  y: number;
  z: number;
  radius: number;
  dimension: string;
  /** A preset id from LOGBOOK_WINDOWS, or 'custom' with explicit bounds. */
  windowId: string;
  after?: number;
  before?: number;
}

export const NEARBY_RADII = [8, 16, 32, 64, 128, 256] as const;

export const DEFAULT_NEARBY_QUERY: Omit<LogbookNearbyQuery, 'serverId' | 'x' | 'y' | 'z'> = {
  radius: 32,
  dimension: 'minecraft:overworld',
  windowId: '24h',
};

/** The same filter state the main search uses, so a nearby answer is one click from the full list. */
export function toNearbyFilters(query: LogbookNearbyQuery): LogbookFilterState {
  return {
    ...DEFAULT_LOGBOOK_FILTERS,
    servers: [query.serverId],
    actions: [],
    dimension: query.dimension,
    windowId: query.windowId,
    after: query.after,
    before: query.before,
    box: boxAround(query.x, query.y, query.z, query.radius),
    limit: 200,
  };
}

export interface LogbookPresence {
  actorId: string;
  actorName: string;
  actorKind: LogbookActorKind;
  /** Every matching record for this actor in the window, from the aggregation. */
  count: number;
  firstSeenAt: number | null;
  lastSeenAt: number | null;
  /** Whether the bounds above are exact (aggregation) or from the loaded sample only. */
  boundsExact: boolean;
  /** Actions taken, most frequent first, from the sample. */
  actions: { action: string; count: number }[];
  /** How close their nearest record came to the centre, in blocks, from the sample. */
  nearestDistance: number | null;
  /** How many of the sample's records were theirs, so a thin sample reads as thin. */
  sampled: number;
}

function distance(event: LogbookEvent, centre: { x: number; y: number; z: number }): number {
  return Math.sqrt((event.x - centre.x) ** 2 + (event.y - centre.y) ** 2 + (event.z - centre.z) ** 2);
}

/**
 * Joins the exact per-actor aggregation with what the sample can add about each person.
 *
 * Ordering is by the aggregation's count, which is the honest measure of presence. An actor that
 * appears in the aggregation but not in the sample still ranks; they simply have no action list yet,
 * which the card says rather than hiding the row.
 */
export function rankPresence(
  byActor: LogbookAggregationResult | null,
  sample: LogbookEvent[],
  centre: { x: number; y: number; z: number },
): LogbookPresence[] {
  const fromSample = new Map<string, {
    name: string; kind: LogbookActorKind; first: number; last: number; nearest: number;
    actions: Map<string, number>; sampled: number;
  }>();

  // Keyed exactly as the aggregation groups: by actor_id, with every unattributed record under ''.
  for (const event of sample) {
    const key = event.actor_id;
    const entry = fromSample.get(key) ?? {
      name: event.actor_name, kind: event.actor_kind, first: Infinity, last: 0, nearest: Infinity,
      actions: new Map<string, number>(), sampled: 0,
    };
    if (event.timestamp > 0) {
      entry.first = Math.min(entry.first, event.timestamp);
      entry.last = Math.max(entry.last, event.timestamp);
    }
    entry.nearest = Math.min(entry.nearest, distance(event, centre));
    entry.actions.set(event.action, (entry.actions.get(event.action) ?? 0) + 1);
    entry.sampled += 1;
    // The most recent name wins, which is the one a moderator will recognise.
    if (event.actor_name) entry.name = event.actor_name;
    // An id shared by two kinds is not one person; say so rather than pick one.
    if (entry.kind !== event.actor_kind) entry.kind = 'unknown';
    fromSample.set(key, entry);
  }

  const sortedActions = (actions: Map<string, number>) =>
    Array.from(actions, ([action, count]) => ({ action, count })).sort((a, b) => b.count - a.count || a.action.localeCompare(b.action));

  const groups = byActor?.groups ?? [];
  const ranked: LogbookPresence[] = groups.map((group) => {
    const actorId = String(group.value);
    const seen = fromSample.get(actorId);
    const exact = typeof group.first_timestamp === 'number' && typeof group.last_timestamp === 'number';
    return {
      actorId,
      actorName: seen?.name ?? '',
      actorKind: seen?.kind ?? (actorId ? 'player' : 'unknown'),
      count: group.event_count,
      firstSeenAt: exact ? group.first_timestamp! : seen && Number.isFinite(seen.first) ? seen.first : null,
      lastSeenAt: exact ? group.last_timestamp! : seen && seen.last > 0 ? seen.last : null,
      boundsExact: exact,
      actions: seen ? sortedActions(seen.actions) : [],
      nearestDistance: seen && Number.isFinite(seen.nearest) ? Math.round(seen.nearest * 10) / 10 : null,
      sampled: seen?.sampled ?? 0,
    };
  });

  // Without an aggregation (an old service, or a failed call) the sample is all there is: say so
  // through boundsExact=false rather than inventing a total.
  if (ranked.length === 0 && fromSample.size > 0) {
    return Array.from(fromSample, ([actorId, seen]) => ({
      actorId,
      actorName: seen.name,
      actorKind: seen.kind,
      count: seen.sampled,
      firstSeenAt: Number.isFinite(seen.first) ? seen.first : null,
      lastSeenAt: seen.last > 0 ? seen.last : null,
      boundsExact: false,
      actions: sortedActions(seen.actions),
      nearestDistance: Number.isFinite(seen.nearest) ? Math.round(seen.nearest * 10) / 10 : null,
      sampled: seen.sampled,
    })).sort((a, b) => b.count - a.count || (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0));
  }

  return ranked.sort((a, b) => b.count - a.count || (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0));
}

/** The presence entries that name a person, as opposed to an automation or nobody. */
export function peopleIn(presence: LogbookPresence[]): LogbookPresence[] {
  return presence.filter((entry) => entry.actorKind === 'player' && entry.actorId !== '');
}

/** How long a presence spanned, for reading: "under a minute", "14 min", "3 h 20 min", "2 d 5 h". */
export function formatSpan(ms: number): string {
  if (ms < 60_000) return 'under a minute';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 === 0 ? `${hours} h` : `${hours} h ${minutes % 60} min`;
  const days = Math.floor(hours / 24);
  return hours % 24 === 0 ? `${days} d` : `${days} d ${hours % 24} h`;
}

// ── URL round trip ──────────────────────────────────────────────────────────

export function nearbyToParams(query: LogbookNearbyQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set('server', query.serverId);
  params.set('at', `${Math.round(query.x)},${Math.round(query.y)},${Math.round(query.z)}`);
  if (query.radius !== DEFAULT_NEARBY_QUERY.radius) params.set('radius', String(query.radius));
  if (query.dimension !== DEFAULT_NEARBY_QUERY.dimension) params.set('dimension', query.dimension);
  if (query.windowId !== DEFAULT_NEARBY_QUERY.windowId) params.set('window', query.windowId);
  if (query.windowId === 'custom') {
    if (query.after != null) params.set('after', String(query.after));
    if (query.before != null) params.set('before', String(query.before));
  }
  return params;
}

/** Null when the link does not name a server and a position: there is nothing to ask without them. */
export function nearbyFromParams(params: URLSearchParams): LogbookNearbyQuery | null {
  const serverId = params.get('server')?.trim();
  const at = params.get('at')?.split(',').map(Number);
  if (!serverId || !at || at.length !== 3 || at.some((value) => !Number.isFinite(value))) return null;
  const radius = Number(params.get('radius'));
  const windowId = params.get('window') ?? DEFAULT_NEARBY_QUERY.windowId;
  const after = Number(params.get('after'));
  const before = Number(params.get('before'));
  return {
    serverId,
    x: at[0], y: at[1], z: at[2],
    radius: (NEARBY_RADII as readonly number[]).includes(radius) ? radius : DEFAULT_NEARBY_QUERY.radius,
    dimension: params.get('dimension') || DEFAULT_NEARBY_QUERY.dimension,
    windowId: windowId === 'custom' || LOGBOOK_WINDOWS.some((entry) => entry.id === windowId) ? windowId : DEFAULT_NEARBY_QUERY.windowId,
    after: Number.isFinite(after) && after > 0 ? after : undefined,
    before: Number.isFinite(before) && before > 0 ? before : undefined,
  };
}

/** A link into the nearby view from anywhere that has a record on screen. */
export function nearbyHrefForEvent(event: LogbookEvent, radius = 32): string {
  // Centre the window on the record: an hour either side is where the surrounding presence is.
  const known = event.timestamp > 0;
  const query: LogbookNearbyQuery = {
    serverId: event.server_id,
    x: event.x, y: event.y, z: event.z,
    radius,
    dimension: event.dimension,
    windowId: known ? 'custom' : DEFAULT_NEARBY_QUERY.windowId,
    after: known ? event.timestamp - 3_600_000 : undefined,
    before: known ? event.timestamp + 3_600_000 : undefined,
  };
  return `/logbook/nearby?${nearbyToParams(query).toString()}`;
}
